<template>
  <material-card
    v-if="systemSettings.levelUnit !== undefined"
    :title="title"
    data-testId="material-card-title"
    color="dover-blue">
    <v-form>
      <v-container
        py-0>
        <v-layout
          wrap>
          <v-flex
            xs12
            md12>
            <v-select
              v-model.number="systemSettings.language"
              :items="languages"
              :label="`${ $t('Console language') }`"
              data-testId="select-Console-language"
              item-text="language"
              item-value="id"
              class="dover-input"
              chips>
              <template
                slot="selection"
                slot-scope="data">
                <v-chip
                  :selected="data.selected"
                  :disabled="data.disabled"
                  :key="data.item.id"
                  data-testId="chip"
                  class="v-chip--select-multi "
                  @input="data.parent.selectItem(data.item)">
                  <v-avatar class="dover-avatar "> {{ data.item.id }}</v-avatar>
                  {{ data.item.language }}
                </v-chip>
              </template>
            </v-select>
          </v-flex>
          <v-flex
            xs6
            md6>
            <span>
              {{ $t('Date formats') }}
            </span>
            <hr>
            <v-radio-group
              v-model="systemSettings.dateFormat"
              :row="true"
              data-testId="radio-group">
              <v-radio
                v-for="(element, index) in dateFormats"
                :key="index"
                :label="element.desc"
                :value="element.code"
                data-testId="radio-element-desc" />
            </v-radio-group>
          </v-flex>
          <v-flex
            xs6
            md6>
            <span>
              {{ $t('TC volume') }}
            </span>
            <hr>
            <v-radio-group
              v-model.number="systemSettings.temperature"
              :row="true"
              data-testId="radio-group-2">
              <v-radio
                v-for="(element, index) in temps"
                :key="index"
                :label="element.desc"
                :value="element.code"
                data-testId="radio-element-desc-2" />
            </v-radio-group>
          </v-flex>
          <v-flex
            xs6
            md4>
            <span>
              {{ $t('Level') }}
            </span>
            <hr>
            <v-radio-group
              v-model.number="systemSettings.levelUnit"
              :row="true"
              data-testId="radio-group-3">
              <v-radio
                v-for="(element, index) in levelUnits"
                :key="index"
                :label="element.desc"
                :value="element.code"
                data-testId="radio-element-desc-3"
                @change="updateHysteresis($event)"/>
            </v-radio-group>
          </v-flex>
          <v-flex
            xs6
            md4>
            <span>
              {{ $t('Volume') }}
            </span>
            <hr>
            <v-radio-group
              v-model.number="systemSettings.volumeUnit"
              :row="true"
              data-testId="radio-group-4">
              <v-radio
                v-for="(element, index) in volumeUnits"
                :key="index"
                :label="element.desc"
                :value="element.code"
                data-testId="radio-element-desc-4" />
            </v-radio-group>
          </v-flex>
          <v-flex
            xs6
            md4>
            <span>
              {{ $t('Temperature') }}
            </span>
            <hr>
            <v-radio-group
              v-model.number="systemSettings.temperatureUnit"
              :row="true"
              data-testId="radio-group-5">
              <v-radio
                v-for="(element, index) in temperatureUnits"
                :key="index"
                :label="element.desc"
                :value="element.code"
                data-testId="radio-element-desc-5"
                @change="updateTemperatureLabels($event)"/>
            </v-radio-group>
          </v-flex>
          <v-flex
            xs6
            md4>
            <span>
              {{ $t('Alarm settings') }}
            </span>
            <hr>
            <v-text-field
              v-model.number="systemSettings.alarmHysteresisLevel"
              :rules="rules.required.concat(rules.range)"
              :counter="6"
              :label="`${ $t('Tolerance ({0})', [levelUnits[systemSettings.levelUnit].desc]) }`"
              data-testId="text-field-Tolerance"
              class="dover-input"/>
          </v-flex>
          <v-flex
            xs6
            md4>
            <span>
              {{ $t('Alarm Buzzer') }}
            </span>
            <hr>
            <v-slider
              v-model.number="alarmDuration"
              :thumb-color="dover-blue"
              :tooltip="always"
              :tick-labels="buzzerTickLabels"
              data-testId="slider-buzzerTick"
              ticks="always"
              tick-size="4"
              step="1"
              min="0"
              max="3"
              always-dirty
              thumb-label/>
          </v-flex>
          <!-- For future use, if required
          <v-flex
            xs6
            md4>
            <v-switch
              v-model="systemSettings.sldOfflineMode"
              :label="`${$t('Offline Mode for SLD')}`"
              data-testId="switch"
              class="dover-input text-xs-center" />
          </v-flex>
          -->
          <v-flex
            xs12
            text-xs-center>
            <v-btn
              v-if="Maglink.IsAdminUser()"
              data-testId="btn-Save"
              small
              class="mx-0 font-weight-light"
              color="dover-blue"
              @click="save">
              {{ $t('Save') }}
            </v-btn>
          </v-flex>
          <v-flex
            xs12
            md12>
            <hr>
            <span
              data-testId="span-Dipswitch-settings"
              class="dover-blue-color"
              v-text="$t('Dipswitch settings')" />
            <hr>
            <v-data-table
              :headers="headers"
              :items="dipswitches"
              :no-data-text="`${ Maglink.NoDataMessage(false) }`"
              :no-results-text="`${ Maglink.NoDataMessage(true) }`"
              data-testId="data-table-Maglink-NoDataMessage"
              hide-actions>
              <template
                slot="headerCell"
                slot-scope="{ header }">
                <span
                  v-text="header.text" />
              </template>
              <template
                v-if="index === 7 || index < (Maglink.isPlus ? 1 : 2)"
                slot="items"
                slot-scope="{ index, item }">
                <td width="10%">{{ index + 1 }}</td>
                <td width="10%">
                  <span
                    class="dover-blue-color"
                    v-text="item.enabled ? $t('On').toUpperCase() : $t('Off').toUpperCase()" />
                </td>
                <td width="20%">{{ $t(item.name) }}</td>
                <td width="40%">{{ getDescription(index, item) }}</td>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
    </v-form>
  </material-card>
</template>

<script>
import Vue from 'vue'
import { i18n } from '@/i18n'

export default {
  props: {
    title: {
      type: String,
      required: true
    }
  },
  data: () => ({
    rules: {
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      range: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 500) ]
    },
    systemSettings: { },
    levelUnits: [ { desc: '' } ],
    languages: [],
    dateFormats: [],
    volumeUnits: [],
    temperatureUnits: [],
    temps: [],
    headers: [
      { sortable: false, text: i18n.t('Position') },
      { sortable: false, text: i18n.t('Status') },
      { sortable: false, text: i18n.t('Name') },
      { sortable: false, text: i18n.t('Description') }
    ],
    dipswitches: [],
    alarmDuration: 0,
    buzzerTickLabels: [
      i18n.t('Mute'),
      i18n.t('Short'),
      i18n.t('Long'),
      i18n.t('Continuous')
    ]
  }),
  computed: {
    hintText () {
      return this.$t('Duration (s) [Max = Continuous]')
    }
  },
  mounted () {
    this.Maglink.GetOptions()
      .then(response => {
        this.Maglink.TranslateOptionNames(response)
        this.dateFormats = response.data.dateFormats
        this.temps = response.data.temperatures
        this.levelUnits = response.data.levelUnits
        this.volumeUnits = response.data.volumeUnits
        this.temperatureUnits = response.data.temperatureUnits

        this.Maglink.GetLanguages()
          .then(resp => {
            resp.data.forEach(item => {
              if (item.enabled) this.languages.push(item)
            })
            this.languages.forEach(item => {
              item.language = this.$t(item.language)
            })
            // sort by desc
            this.languages.sort((a, b) => (a.language > b.language) ? 1 : -1)
          })
          .catch(error => (this.snack('error', error.message)))

        this.Maglink.GetSettings('SystemSettings')
          .then(response => {
            this.systemSettings = { ...this.systemSettings, ...response.data }
            Vue.prototype.Maglink.SystemSettings = response.data
            this.systemSettings = this.Maglink.ToDisplay(this.systemSettings, this.systemSettings.levelUnit)
            this.updateTemperatureLabels(this.systemSettings.temperatureUnit)
            this.alarmDuration = this.systemSettings.alarmDuration < 1 ? 0 : this.systemSettings.alarmDuration < 60 ? 1 : this.systemSettings.alarmDuration < 250 ? 2 : 3
          })
          .catch(error => (this.snack('error', error.message)))
      })
      .catch(error => (this.snack('error', error.message)))

    this.Maglink.GetSettings('Dipswitches')
      .then(
        response => {
          this.dipswitches = response.data
          this.dipswitches[7].name = this.$t('W&M')
        }
      )
      .catch(error => (this.snack('error', error.message)))
  },
  methods: {
    getDescription (index, item) {
      if (index === 0) return this.$t('OFF: Normal; OK: Inverted')
      if (index === 1) return this.$t('OFF: Disabled; OK: Enabled')
      if (index === 7) return this.$t('OFF: Inactive; OK: W&M approved')
    },
    updateHysteresis (toUnit) {
      let value = this.systemSettings.alarmHysteresisLevel
      value = this.Maglink.LevelFromDisplay(value, this.systemSettings.levelUnit)
      value = this.Maglink.LevelToDisplay(value, toUnit)
      this.systemSettings.alarmHysteresisLevel = parseFloat(this.Maglink.LevelFormat(value, toUnit))
    },
    updateTemperatureLabels (toUnit) {
      let tempUdm = this.$t(this.temperatureUnits.find(c => c.code === toUnit).desc)
      for (let index = 0; index < this.temps.length; index++) {
        let item = this.temps[index]
        item.desc = this.Maglink.RoundFormat(this.Maglink.TemperatureToDisplay(item.code, toUnit), 1)
        item.desc += ' ' + tempUdm
      }
    },
    save (event) {
      this.systemSettings.alarmDuration = this.alarmDuration > 2 ? 255 : this.alarmDuration > 1 ? 180 : this.alarmDuration > 0 ? 30 : 0
      this.Maglink.SetSettings('SystemSettings', this.Maglink.FromDisplay(this.systemSettings, this.systemSettings.levelUnit))
        .then(response => {
          Vue.prototype.Maglink.SystemSettings = this.systemSettings
          this.snack('success', response.data['done'])
        })
        .catch(error => (this.snack('error', error.message)))

      this.$emit('settingsSaved')
    },
    snack (color, message) {
      message = this.title + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
